/**
 * Centralized error messages for better user experience
 * Maps technical errors to user-friendly messages
 */

export const ERROR_MESSAGES = {
  // Authentication errors
  AUTH: {
    INVALID_CREDENTIALS: 'The email or password you entered is incorrect. Please try again.',
    EMAIL_NOT_VERIFIED: 'Please verify your email address before signing in. Check your inbox for the verification link.',
    SESSION_EXPIRED: 'Your session has expired. Please sign in again.',
    UNAUTHORIZED: 'You need to be signed in to access this feature.',
    ACCOUNT_EXISTS: 'An account with this email already exists. Try signing in instead.',
    WEAK_PASSWORD: 'Password must be at least 8 characters long and include letters and numbers.',
    INVALID_EMAIL: 'Please enter a valid email address.',
    TOKEN_REFRESH_FAILED: 'Your session has expired. Please sign in again.',
  },

  // Form errors
  FORM: {
    NOT_FOUND: 'We couldn\'t find this form. It may have been deleted or the link is incorrect.',
    ALREADY_SUBMITTED: 'You\'ve already submitted a response to this form. Thank you!',
    SAVE_FAILED: 'We couldn\'t save your changes. Please check your connection and try again.',
    PUBLISH_FAILED: 'We couldn\'t publish your form. Please try again in a moment.',
    DELETE_FAILED: 'We couldn\'t delete this form. Please try again.',
    DUPLICATE_FAILED: 'We couldn\'t duplicate this form. Please try again.',
    LOAD_FAILED: 'We couldn\'t load this form. Please refresh the page and try again.',
    EMPTY_TITLE: 'Please give your form a title before publishing.',
    NO_QUESTIONS: 'Please add at least one question before publishing your form.',
    INVALID_QUESTION: 'Some questions are incomplete. Please check and complete all required fields.',
  },

  // Response errors
  RESPONSE: {
    SUBMIT_FAILED: 'We couldn\'t submit your response. Please try again.',
    LOAD_FAILED: 'We couldn\'t load the responses. Please refresh the page.',
    DELETE_FAILED: 'We couldn\'t delete this response. Please try again.',
    REQUIRED_FIELD: 'This question is required. Please provide an answer.',
    INVALID_EMAIL: 'Please enter a valid email address.',
    INVALID_NUMBER: 'Please enter a valid number.',
    INVALID_URL: 'Please enter a valid website URL (e.g., https://example.com).',
  },

  // Upload errors
  UPLOAD: {
    FILE_TOO_LARGE: 'This file is too large. Please choose a file smaller than 5MB.',
    INVALID_FILE_TYPE: 'This file type is not supported. Please upload an image (JPG, PNG, or GIF).',
    UPLOAD_FAILED: 'We couldn\'t upload this file. Please try again.',
  },

  // Network errors
  NETWORK: {
    NO_CONNECTION: 'No internet connection. Please check your network and try again.',
    TIMEOUT: 'The request took too long. Please check your connection and try again.',
    SERVER_ERROR: 'Something went wrong on our end. Please try again in a moment.',
    RATE_LIMIT: 'Too many requests. Please wait a moment and try again.',
  },

  // Billing errors
  BILLING: {
    PAYMENT_FAILED: 'Your payment couldn\'t be processed. Please check your payment details and try again.',
    SUBSCRIPTION_FAILED: 'We couldn\'t update your subscription. Please try again or contact support.',
    PLAN_LIMIT_REACHED: 'You\'ve reached your plan limit. Please upgrade to continue.',
  },

  // AI errors
  AI: {
    GENERATION_FAILED: 'We couldn\'t generate questions from your text. Please try rephrasing or try again.',
    NO_TEXT_PROVIDED: 'Please paste some text to generate questions from.',
    TIMEOUT: 'The AI is taking longer than usual. Please try again with less text.',
  },

  // Generic fallback
  GENERIC: 'Something went wrong. Please try again or contact support if the problem persists.',
};

/**
 * Get user-friendly error message from error object or code
 * @param {Error|string} error - Error object or error code
 * @param {string} fallback - Fallback message if no match found
 * @returns {string} User-friendly error message
 */
export const getUserFriendlyError = (error, fallback = ERROR_MESSAGES.GENERIC) => {
  // If error is a string, return it directly
  if (typeof error === 'string') {
    return error;
  }

  // Extract error message from error object
  const errorMessage = error?.message || error?.error || error?.data?.message || '';
  const errorCode = error?.code || error?.status;

  // Network errors
  if (error?.code === 'ECONNABORTED' || errorMessage.includes('timeout')) {
    return ERROR_MESSAGES.NETWORK.TIMEOUT;
  }
  if (error?.code === 'ERR_NETWORK' || errorMessage.includes('Network Error')) {
    return ERROR_MESSAGES.NETWORK.NO_CONNECTION;
  }

  // HTTP status codes
  switch (errorCode) {
    case 401:
      return ERROR_MESSAGES.AUTH.SESSION_EXPIRED;
    case 403:
      return ERROR_MESSAGES.AUTH.UNAUTHORIZED;
    case 404:
      if (errorMessage.includes('form')) return ERROR_MESSAGES.FORM.NOT_FOUND;
      return fallback;
    case 409:
      return ERROR_MESSAGES.AUTH.ACCOUNT_EXISTS;
    case 413:
      return ERROR_MESSAGES.UPLOAD.FILE_TOO_LARGE;
    case 429:
      return ERROR_MESSAGES.NETWORK.RATE_LIMIT;
    case 500:
    case 502:
    case 503:
      return ERROR_MESSAGES.NETWORK.SERVER_ERROR;
    default:
      break;
  }

  // Check message content for specific errors
  const lowerMessage = errorMessage.toLowerCase();

  // Auth errors
  if (lowerMessage.includes('invalid email') || lowerMessage.includes('invalid login')) {
    return ERROR_MESSAGES.AUTH.INVALID_CREDENTIALS;
  }
  if (lowerMessage.includes('email not confirmed') || lowerMessage.includes('verify your email')) {
    return ERROR_MESSAGES.AUTH.EMAIL_NOT_VERIFIED;
  }
  if (lowerMessage.includes('password') && lowerMessage.includes('weak')) {
    return ERROR_MESSAGES.AUTH.WEAK_PASSWORD;
  }
  if (lowerMessage.includes('already exists') || lowerMessage.includes('duplicate')) {
    return ERROR_MESSAGES.AUTH.ACCOUNT_EXISTS;
  }
  if (lowerMessage.includes('session expired') || lowerMessage.includes('token')) {
    return ERROR_MESSAGES.AUTH.SESSION_EXPIRED;
  }

  // Form errors
  if (lowerMessage.includes('form not found')) {
    return ERROR_MESSAGES.FORM.NOT_FOUND;
  }
  if (lowerMessage.includes('already submitted')) {
    return ERROR_MESSAGES.FORM.ALREADY_SUBMITTED;
  }

  // Upload errors
  if (lowerMessage.includes('file too large') || lowerMessage.includes('file size')) {
    return ERROR_MESSAGES.UPLOAD.FILE_TOO_LARGE;
  }
  if (lowerMessage.includes('file type') || lowerMessage.includes('unsupported')) {
    return ERROR_MESSAGES.UPLOAD.INVALID_FILE_TYPE;
  }

  // AI errors
  if (lowerMessage.includes('ai') && lowerMessage.includes('failed')) {
    return ERROR_MESSAGES.AI.GENERATION_FAILED;
  }

  // If we have a readable message, use it
  if (errorMessage && errorMessage.length > 0 && errorMessage.length < 200) {
    // Clean up technical jargon
    return errorMessage
      .replace(/Error:/gi, '')
      .replace(/Exception:/gi, '')
      .replace(/Failed to fetch/gi, 'Connection error. Please try again')
      .trim();
  }

  // Return fallback
  return fallback;
};

/**
 * Log error for debugging while showing user-friendly message
 * @param {Error} error - Error object
 * @param {string} context - Context where error occurred
 * @returns {string} User-friendly error message
 */
export const logAndGetError = (error, context = '') => {
  // Log full error for debugging
  console.error(`[${context}] Error:`, error);

  // Return user-friendly message
  return getUserFriendlyError(error);
};
